import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CollectionFilters, ActiveFilter } from '../types/filters'
import { DEFAULT_PAGE_SIZE, WRAPPER_TYPE_OPTIONS, STRENGTH_LEVEL_OPTIONS, STATUS_OPTIONS, ORIGIN_OPTIONS } from '../constants/filterOptions'

const FILTER_LABELS: Record<string, Record<string, string>> = {
  wrapperType: Object.fromEntries(WRAPPER_TYPE_OPTIONS.map(o => [o.value, o.label])),
  strengthLevel: Object.fromEntries(STRENGTH_LEVEL_OPTIONS.map(o => [o.value, o.label])),
  status: Object.fromEntries(STATUS_OPTIONS.map(o => [o.value, o.label])),
  origin: Object.fromEntries(ORIGIN_OPTIONS.map(o => [o.value, o.label])),
}

const FILTER_DISPLAY_NAMES: Partial<Record<keyof CollectionFilters, string>> = {
  search: 'Search',
  brandId: 'Brand',
  wrapperType: 'Wrapper',
  strengthLevel: 'Strength',
  vitolaId: 'Vitola',
  status: 'Status',
  origin: 'Origin',
  purchaseDateStart: 'From Date',
  purchaseDateEnd: 'To Date',
}

export function useCollectionFilters() {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters: CollectionFilters = {
    search: searchParams.get('search') || undefined,
    brandId: searchParams.get('brandId') || undefined,
    wrapperType: searchParams.get('wrapperType') || undefined,
    strengthLevel: searchParams.get('strengthLevel') || undefined,
    vitolaId: searchParams.get('vitolaId') || undefined,
    status: searchParams.get('status') || undefined,
    origin: searchParams.get('origin') || undefined,
    purchaseDateStart: searchParams.get('purchaseDateStart') || undefined,
    purchaseDateEnd: searchParams.get('purchaseDateEnd') || undefined,
    sortBy: (searchParams.get('sortBy') as CollectionFilters['sortBy']) || 'brand',
    sortOrder: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'asc',
    page: parseInt(searchParams.get('page') || '1', 10),
    limit: parseInt(searchParams.get('limit') || String(DEFAULT_PAGE_SIZE), 10),
  }

  const setFilter = useCallback(
    (key: keyof CollectionFilters, value: string | number | undefined) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (value === undefined || value === null || value === '') {
          next.delete(key)
        } else {
          next.set(key, String(value))
        }
        // Reset page when filters change (except page itself)
        if (key !== 'page') {
          next.set('page', '1')
        }
        return next
      })
    },
    [setSearchParams]
  )

  const setFilters = useCallback(
    (updates: Partial<CollectionFilters>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        Object.entries(updates).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            next.delete(key)
          } else {
            next.set(key, String(value))
          }
        })
        next.set('page', '1')
        return next
      })
    },
    [setSearchParams]
  )

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams())
  }, [setSearchParams])

  const activeFilters: ActiveFilter[] = []
  const filterKeys: (keyof CollectionFilters)[] = [
    'search', 'brandId', 'wrapperType', 'strengthLevel', 'vitolaId',
    'status', 'origin', 'purchaseDateStart', 'purchaseDateEnd'
  ]

  filterKeys.forEach((key) => {
    const value = filters[key]
    if (value !== undefined && value !== '') {
      const displayName = FILTER_DISPLAY_NAMES[key] || key
      const labelMap = FILTER_LABELS[key]
      const displayValue = labelMap ? (labelMap[value as string] || value) : value
      activeFilters.push({
        key,
        label: `${displayName}: ${displayValue}`,
        value: value as string,
      })
    }
  })

  const hasActiveFilters = activeFilters.length > 0

  return {
    filters,
    setFilter,
    setFilters,
    clearFilters,
    activeFilters,
    hasActiveFilters,
  }
}
